import { DefaultButton, Dialog, DialogContent, DialogFooter, DialogType, IDialogContentProps, IPanelStyles, mergeStyles, Panel, PanelType, PrimaryButton, ScrollablePane, ScrollbarVisibility, Spinner, SpinnerSize, Stack, TextField } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { CheckinTypes, MetadataDto, MetadataUpdateRequest } from "../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import { useLazyCheckinItemQuery, useLazyDeleteVersionByLabelQuery, useLazyGetItemVersionsQuery, useLazyPublishItemQuery } from "../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { IVersionSelectorProps, VersionSelector } from "../../src/taskpane/components/_v2/_shared/VersionSelector/VersionSelector";
import { SharePointInputData } from "../../src/taskpane/interfaces/props/SharePointInputData";
import authModule from "../../utilities/AuthModule";
import generalModule from "../../utilities/GeneralModule";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { logger } from "../../utilities/logger";
import { getFileVersionData, getSharePointDocumentLibraryFields } from "../../src/taskpane/services/SharePointRestServices";
import { piiConfig, SharepointFilters } from "../../src/taskpane/config/constants";
import { BaseSharepointHelper } from "../../src/taskpane/components/helpers/sharepointContentTypeHelper";
import InputMetaDataFactory from "../../src/taskpane/components/directoryComponents/InputMetadataFactory";
import { SharepointSpServices } from "../../src/taskpane/services/SharepointSpServices";
import Loader from "../../src/taskpane/components/Loader";
import { SharePointMetadataBooleans } from "../../src/taskpane/config/sharepointConstants";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { apiUpdateItemMetadata } from "../../src/taskpane/services/ApiService";

const dialogContentProps: IDialogContentProps = {
    type: DialogType.normal,
    showCloseButton: true,
    closeButtonAriaLabel: 'Close',
};

export const scrollStyle = mergeStyles({
    position: "relative",
    height: "calc(100vh - 160px)",
});


export const panelButtonsContainer = mergeStyles({
    float: "right",
    paddingTop: 5
});

const panelStyles: Partial<IPanelStyles> = {
    content: {
        paddingLeft: 10,
        paddingRight: 10
    }
}

export interface ICMCheckinModalProps {
    selectedItems?: any[];
    selectedAddInItems?: any[];
    showDialog: boolean;
    isInOneDrive: boolean;
    isPublish?: boolean;
    closeDialog: () => void;
    closeOnSuccess: (updatedInputValues: SharePointInputData[]) => void;
    fileUpdatePublishInfo?: IFileUpdatePublishProps;
    updateOfficeXML?: () => Promise<void>;
}

export interface IFileUpdatePublishProps {
    request: MetadataUpdateRequest;
    originalInputValues: SharePointInputData[];
}

export const CMCheckinModal = ({ selectedItems, selectedAddInItems, showDialog, isInOneDrive, isPublish, closeDialog, closeOnSuccess, fileUpdatePublishInfo, updateOfficeXML }: ICMCheckinModalProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
    const requiredMetadataOnPublish = useSelector((state: RootState) => state.tenantInfo.requiredMetadataOnPublish);
    const autoDeleteMinorVersionsOnPublish = useSelector((state: RootState) => state.tenantInfo.autoDeleteMinorVersionsOnPublish);
    const regionalSettings = useSelector((state: RootState) => state.regionalSettings);

    const [versionProps, setVersionProps] = React.useState<IVersionSelectorProps>();
    const [checkinType, setCheckinType] = React.useState<CheckinTypes>(CheckinTypes.majorCheckIn);
    const [comment, setComment] = React.useState('');
    const [checkinError, setCheckinError] = React.useState('');
    const [isBusy, setIsBusy] = React.useState(false);
    const [hasError, setHasError] = React.useState(false);

    const [inputValuesToSave, setInputValuesToSave] = React.useState<SharePointInputData[]>([]);
    const [sharepointService, setSharepointService] = React.useState<SharepointSpServices>(null);
    const [fields, setFields] = React.useState<any[]>([]);
    const [retrievingInformation, setRetrievingInformation] = React.useState(false);
    const [updateMetaDepsLoaded, setUpdateMetaDepsLoaded] = React.useState(false);

    const [checkinItem] = useLazyCheckinItemQuery();
    const [publishItem] = useLazyPublishItemQuery();
    const [getItemVersions] = useLazyGetItemVersionsQuery();
    const [deleteVersionByLabel] = useLazyDeleteVersionByLabelQuery();

    React.useEffect(() => {
        if (requiredMetadataOnPublish) {
            (async () => {
                const metaFieldsOnPublish: string[] = requiredMetadataOnPublish?.split(",");
                setRetrievingInformation(true);

                const sharepointSpService = new SharepointSpServices(fileUpdatePublishInfo ? fileUpdatePublishInfo.request.siteUrl : currentDirectory.webUrl, fileUpdatePublishInfo ? fileUpdatePublishInfo.request.accessToken : await authModule.getSharePointAccessToken());
                setSharepointService(sharepointSpService);

                let result = await getSharePointDocumentLibraryFields(
                    await authModule.getSharePointAccessToken(),
                    currentDirectory.webUrl,
                    currentSharepointFolder.sharepointListId,
                    SharepointFilters.metadata.search
                );

                let fields = result
                    .map(BaseSharepointHelper.convertMetadataToFields)
                    .filter(field => field && metaFieldsOnPublish.includes(field.InternalName))
                    .map(field => {
                        return {
                            ...field,
                            xml: {
                                ...field.xml,
                                Required: "TRUE"
                            }
                        };
                    });

                if (fields?.length > 0) {
                    setFields(fields);
                }
                else {
                    setRetrievingInformation(false);
                }
            })();
        }
    }, [requiredMetadataOnPublish, fileUpdatePublishInfo]);

    React.useEffect(() => {
        if (requiredMetadataOnPublish && fields?.length > 0) {
            (async () => {
                const metaFieldsOnPublish: string[] = requiredMetadataOnPublish?.split(",");
                if (!updateMetaDepsLoaded) {
                    if (fileUpdatePublishInfo && fileUpdatePublishInfo.originalInputValues?.length > 0) {
                        setInputValuesToSave(fileUpdatePublishInfo.originalInputValues.map(inputValue => {
                            return {
                                ...inputValue,
                                value: metaFieldsOnPublish.includes(inputValue.internalName) ? null : inputValue.value,
                                hasError: metaFieldsOnPublish.includes(inputValue.internalName) ? true : false
                            }
                        }));
                    } else {
                        let inputValuesToSaveFields: SharePointInputData[] = fields.map((item, index) => {
                            return setupInputValues(item, index);
                        });
                        setInputValuesToSave(inputValuesToSaveFields);
                    }
                    setRetrievingInformation(false);
                    setUpdateMetaDepsLoaded(true);
                }
            })();
        }
    }, [requiredMetadataOnPublish, fields, fileUpdatePublishInfo, updateMetaDepsLoaded]);

    React.useEffect(() => {
        setHasError(inputValuesToSave.some(value => value.hasError));
    }, [inputValuesToSave]);

    React.useEffect(() => {
        if (currentSharepointFolder?.enableMinorVersions === true && selectedItems?.length > 0) {
            setCheckinType(CheckinTypes.minorCheckIn);
            (async () => {
                const fileVersionData = await getFileVersionData(
                    isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                    currentDirectory?.webUrl,
                    selectedItems[0].UniqueId?.replace("{", "").replace("}", "")
                );

                if (fileVersionData) {
                    setVersionProps({
                        multipleFilesSelected: selectedItems?.length > 1,
                        level: fileVersionData.Level,
                        majorVersion: fileVersionData.MajorVersion,
                        minorVersion: fileVersionData.MinorVersion
                    });
                }
            })();
        }
    }, [selectedItems]);

    const setupInputValues = (item, index): SharePointInputData => {
        return {
            index: index,
            internalName: item.InternalName,
            value: null,
            hasError: item.xml.Required === SharePointMetadataBooleans.true,
            type: item.xml.Percentage === SharePointMetadataBooleans.true ? `${item.xml.Type}Percentage` : item.xml.Type,
            displayName: item.xml.DisplayName,
            xml: item.xml,
            choices: item.Choices
        };
    };

    const onConfirmCheckin = async () => {
        setIsBusy(true);
        setCheckinError('');

        let listItemUniqueId;

        if (selectedItems && selectedItems.length > 0) {
            listItemUniqueId = selectedItems[0]?.uniqueid ?? selectedItems[0]?.UniqueId?.replace('{', '').replace('}', '')
        }
        else if (selectedAddInItems && selectedAddInItems.length > 0) {
            listItemUniqueId = selectedAddInItems[0]?.UniqueId ? selectedAddInItems[0]?.UniqueId : selectedAddInItems[0]?.sharepointIds?.listItemUniqueId
        }

        if (requiredMetadataOnPublish) {
            if (!fileUpdatePublishInfo) {
                let isCurrentDirectorySPList = workingDirectories && workingDirectories.some(x => x.type === ComponentTypes.SharePointList)

                let request: MetadataUpdateRequest = {
                    accessToken: await authModule.getSharePointAccessToken(),
                    siteUrl: currentDirectory.webUrl,
                    listId: currentDirectory.sharePointListId,
                    listItemUniqueId,
                    isFolder: false,
                    isListItem: isCurrentDirectorySPList ? true : false,
                    listSettings: {
                        enableMinorVersions: currentSharepointFolder?.enableMinorVersions === true,
                    },
                    metadata: inputValuesToSave.map(input => {
                        const meta: MetadataDto = {
                            type: input.type,
                            internalName: input.internalName,
                            value: !(input?.value === 0) && (!input?.value || (Array.isArray(input?.value) && !input?.value?.length)) ? undefined : input.value
                        }
                        return meta;
                    })
                }

                if (updateOfficeXML) await updateOfficeXML();
                await apiUpdateItemMetadata(request);
            } else {
                try {
                    let newRequest: MetadataUpdateRequest;

                    // inputValuesToSave will have a length if there are any requered meta data fields on publish, other wise we will use the passed through request
                    if (inputValuesToSave?.length > 0) {
                        let newRequest: MetadataUpdateRequest = {
                            ...fileUpdatePublishInfo.request,
                            metadata: inputValuesToSave.map(input => {
                                const meta: MetadataDto = {
                                    type: input.type,
                                    internalName: input.internalName,
                                    value: !(input?.value === 0) && (!input?.value || (Array.isArray(input?.value) && !input?.value?.length)) ? undefined : input.value
                                }
                                return meta;
                            })
                        }
        
                        const contentTypeMeta = fileUpdatePublishInfo.request.metadata.find(x => x.type === "Content");
                        if (contentTypeMeta) {
                            newRequest.metadata.unshift(contentTypeMeta);
                        }
                    }
    
                    if (updateOfficeXML) await updateOfficeXML();
                    await apiUpdateItemMetadata(newRequest ?? fileUpdatePublishInfo.request);
                } catch (error) {
                    logger(error, "CMCheckinModal.tsx (onConfirmCheckin)");
                }
            }
        }

        if (!isPublish) {
            try {
                if (generalModule.isInConentMangerMode()) {
                    await Promise.all(selectedItems.map(async item => {
                        await checkinItem({
                            siteUrl: isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                            uniqueId: item.UniqueId.replace("{", "").replace("}", ""),
                            accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                            checkinType,
                            comment
                        }).unwrap();
                    }));
                }
                else {
                    await Promise.all(selectedAddInItems.map(async item => {
                        await checkinItem({
                            siteUrl: isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                            uniqueId: item.UniqueId,
                            accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                            checkinType,
                            comment
                        }).unwrap();
                    }));
                }
            } catch (error) {
                logger(error, "CMCheckinModal.tsx (onConfirmCheckin)");
                setCheckinError(error?.data?.['odata.error']?.message?.value);
            }
        } else {
            try {
                if (generalModule.isInConentMangerMode()) {
                    await Promise.all(selectedItems.map(async item => {
                        await publishItem({
                            siteUrl: isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                            uniqueId: item.UniqueId.replace("{", "").replace("}", ""),
                            accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                            comment
                        }).unwrap();
                    }));
                }
                else {
                    await Promise.all(selectedAddInItems.map(async item => {
                        await publishItem({
                            siteUrl: isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                            uniqueId: item.UniqueId ? item.UniqueId : item.sharepointIds.listItemUniqueId,
                            accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                            comment
                        }).unwrap();
                    }));
                }
            } catch (error) {
                logger(error, "CMCheckinModal.tsx (onConfirmCheckin - publish)");
                setCheckinError(error?.data?.['odata.error']?.message?.value);
            }
        }

        if (autoDeleteMinorVersionsOnPublish) {
            try {
                const itemVersions = await getItemVersions({
                    siteUrl: currentDirectory?.webUrl,
                    uniqueId: listItemUniqueId,
                    accessToken: await authModule.getSharePointAccessToken(),
                }).unwrap();

                const versions = itemVersions.d?.results;

                if (versions && versions.length > 0) {
                    await Promise.all(versions.map(async version => {
                        if (!version.IsCurrentVersion && version.ID % 512 > 0) {
                            deleteVersionByLabel({
                                siteUrl: isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                                uniqueId: listItemUniqueId,
                                accessToken: await authModule.getSharePointAccessToken(),
                                versionLabel: version.VersionLabel
                            }).unwrap();
                        }
                    }));
                }
            } catch (error) {
                logger(error, "CMCheckinMoadal.tsx (autoDeleteMinorVersionsOnPublish)");
            }
        }

        setIsBusy(false);
        closeOnSuccess(inputValuesToSave);
    }

    const checkinTypeChanged = (checkinType: CheckinTypes) => {
        setCheckinType(checkinType);
    }

    const onCommentChanged = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setComment(newValue);
    }

    const updateSharepointMetadataToSave = (metadata: SharePointInputData[], informationProtectionId: string, changedIndex?: number) => {
        setInputValuesToSave(metadata);
    };

    const handleInputChange = (index: number, value: any, hasError: boolean) => {
        const updatedinputValuesToSave = [...inputValuesToSave];
        updatedinputValuesToSave[index].value = value;
        updatedinputValuesToSave[index].hasError = hasError;
        updateSharepointMetadataToSave(updatedinputValuesToSave, null, index);
    };

    const onRenderFooterContent = React.useCallback(
        () => (
            <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }}>
                <DefaultButton onClick={() => closeDialog()} text={languageStrings.Cancel} disabled={isBusy} style={{ marginRight: "5px" }} />
                <PrimaryButton onClick={() => onConfirmCheckin()} text={isPublish ? languageStrings.Publish : languageStrings.CheckIn} disabled={isBusy || hasError}>
                    {isBusy && <Spinner size={SpinnerSize.small} style={{ paddingLeft: '5px' }} />}
                </PrimaryButton>
            </Stack>
        ), [isBusy, hasError, comment, inputValuesToSave]);

    return (<React.Fragment>
        {!generalModule.isInConentMangerMode() && <Panel
            isOpen={showDialog}
            headerText={isPublish ? languageStrings.Publish : languageStrings.CheckIn}
            onDismiss={() => closeDialog()}
            isLightDismiss
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={!generalModule.isInConentMangerMode() ? "300px" : "450px"}
            styles={panelStyles}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            <div className={scrollStyle} >
                <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                    <div className="xb-content">
                        {versionProps && !isPublish && <React.Fragment>
                            <VersionSelector level={versionProps.level} majorVersion={versionProps.majorVersion} minorVersion={versionProps.minorVersion} checkinTypeChanged={checkinTypeChanged} multipleFilesSelected={selectedItems?.length > 1} />
                        </React.Fragment>}
                        {retrievingInformation && !isBusy && <Loader label={languageStrings.RetrievingInfo} />}
                        {updateMetaDepsLoaded &&
                            <Stack tokens={{ childrenGap: 15 }} style={{ marginBottom: "15px" }}>
                                {fields.map((item, index, _arr) => {
                                    let inputValueIndex = inputValuesToSave.findIndex(x => x.internalName === item.InternalName)
                                    return inputValueIndex !== -1 && item.InternalName !== piiConfig.privacySentitiveColumnName && item.InternalName !== piiConfig.privacySentitiveDataColumnName ? (
                                        <Stack.Item key={index} >
                                            <InputMetaDataFactory
                                                xml={item.xml}
                                                choices={item.Choices}
                                                webUrl={fileUpdatePublishInfo ? fileUpdatePublishInfo.request.siteUrl : currentDirectory.webUrl}
                                                sharepointService={sharepointService}
                                                handleInputChange={handleInputChange}
                                                inputData={inputValuesToSave[inputValueIndex]}
                                                internalName={item.InternalName}
                                                regionalSetting={regionalSettings[fileUpdatePublishInfo ? fileUpdatePublishInfo.request.siteUrl : currentDirectory.webUrl]}
                                                sharepointListId={fileUpdatePublishInfo ? fileUpdatePublishInfo.request.listId : currentDirectory.sharePointListId}
                                                datasourceFields={[]}
                                            />
                                        </Stack.Item>
                                    ) : null;
                                })}
                            </Stack>}
                        <div style={{ paddingTop: versionProps && !isPublish ? '15px' : '0' }}>
                            <TextField label={languageStrings.Comment} multiline rows={3} value={comment} onChange={onCommentChanged} errorMessage={checkinError} />
                        </div>
                    </div>
                </ScrollablePane>
            </div>

        </Panel>}

        {generalModule.isInConentMangerMode() && <Dialog hidden={!showDialog} onDismiss={() => closeDialog()} type={DialogType.normal} title={isPublish ? languageStrings.Publish : languageStrings.CheckIn} dialogContentProps={dialogContentProps} minWidth={600} maxWidth={600}>
            <DialogContent styles={{ inner: { padding: 0 }, header: { display: 'none' } }}>
                {versionProps && !isPublish && <React.Fragment>
                    <VersionSelector level={versionProps.level} majorVersion={versionProps.majorVersion} minorVersion={versionProps.minorVersion} checkinTypeChanged={checkinTypeChanged} multipleFilesSelected={selectedItems?.length > 1} />
                </React.Fragment>}
                {retrievingInformation && !isBusy && <Loader label={languageStrings.RetrievingInfo} />}
                {updateMetaDepsLoaded &&
                    <Stack tokens={{ childrenGap: 15 }} style={{ marginBottom: "15px" }}>
                        {fields.map((item, index, _arr) => {
                            let inputValueIndex = inputValuesToSave.findIndex(x => x.internalName === item.InternalName)
                            return inputValueIndex !== -1 && item.InternalName !== piiConfig.privacySentitiveColumnName && item.InternalName !== piiConfig.privacySentitiveDataColumnName ? (
                                <Stack.Item key={index} >
                                    <InputMetaDataFactory
                                        xml={item.xml}
                                        choices={item.Choices}
                                        webUrl={fileUpdatePublishInfo ? fileUpdatePublishInfo.request.siteUrl : currentDirectory.webUrl}
                                        sharepointService={sharepointService}
                                        handleInputChange={handleInputChange}
                                        inputData={inputValuesToSave[inputValueIndex]}
                                        internalName={item.InternalName}
                                        regionalSetting={regionalSettings[fileUpdatePublishInfo ? fileUpdatePublishInfo.request.siteUrl : currentDirectory.webUrl]}
                                        sharepointListId={fileUpdatePublishInfo ? fileUpdatePublishInfo.request.listId : currentDirectory.sharePointListId}
                                        datasourceFields={[]}
                                    />
                                </Stack.Item>
                            ) : null;
                        })}
                    </Stack>}
                <div style={{ paddingTop: versionProps && !isPublish ? '15px' : '0' }}>
                    <TextField label={languageStrings.Comment} multiline rows={3} value={comment} onChange={onCommentChanged} errorMessage={checkinError} />
                </div>
            </DialogContent>
            <DialogFooter>
                <DefaultButton onClick={() => closeDialog()} text={languageStrings.Cancel} disabled={isBusy} />
                <PrimaryButton onClick={() => onConfirmCheckin()} text={isPublish ? languageStrings.Publish : languageStrings.CheckIn} disabled={isBusy || hasError}>
                    {isBusy && <Spinner size={SpinnerSize.small} style={{ paddingLeft: '5px' }} />}
                </PrimaryButton>
            </DialogFooter>
        </Dialog>}
    </React.Fragment>

    )
};